import {
  Box,
  Chip,
  Divider,
  Skeleton,
  Switch,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectExplorationModeByCourseID,
  setExplorationMode,
} from 'src/v3/classroom-v2/classroom-v2-wizard.slice'
import {
  ConfirmIcon,
  FinalMilestoneIcon,
  LockIcon,
  PlayArrowIcon,
  ShowNotesIcon,
} from 'src/v3/core/icons'
import { CourseV2Content, CourseV2Step } from 'src/v3/classroom-v2/types'

type StepStatus = 'completed' | 'active' | 'unlocked' | 'locked'

let stepTypeLabel = (step: CourseV2Step) =>
  step.type === 'video' ? 'Video' : 'Article'

let StepIcon = ({
  step,
  status,
}: {
  step: CourseV2Step
  status: StepStatus
}) => {
  let theme = useTheme()

  if (status === 'completed') {
    return (
      <ConfirmIcon
        fontSize="small"
        sx={{ color: 'success.main', flexShrink: 0 }}
      />
    )
  }

  if (status === 'locked') {
    return (
      <LockIcon
        fontSize="small"
        sx={{ color: 'text.disabled', flexShrink: 0 }}
      />
    )
  }

  let color =
    status === 'active'
      ? theme.palette.primary.main
      : theme.palette.text.secondary

  if (step.type === 'video') {
    return <PlayArrowIcon fontSize="small" sx={{ color, flexShrink: 0 }} />
  }
  return <ShowNotesIcon fontSize="small" sx={{ color, flexShrink: 0 }} />
}

let StepRow = ({
  step,
  stepNumber,
  status,
  arrayIndex,
  explorationMode,
  onSelectStep,
}: {
  step: CourseV2Step
  stepNumber: number
  status: StepStatus
  arrayIndex: number
  explorationMode: boolean
  onSelectStep: (arrayIndex: number) => void
}) => {
  let theme = useTheme()
  let isActive = status === 'active'
  let isLocked = status === 'locked' && !explorationMode
  let accentColor = explorationMode
    ? theme.palette.warning.main
    : theme.palette.primary.main

  let handleClick = () => {
    if (isLocked) return
    onSelectStep(arrayIndex)
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      disabled={isLocked}
      aria-current={isActive ? 'step' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
        gap: 1.5,
        px: 1.5,
        py: 1.5,
        border: 0,
        borderLeft: isActive
          ? `3px solid ${accentColor}`
          : '3px solid transparent',
        borderRadius: 1,
        font: 'inherit',
        textAlign: 'left',
        color: 'inherit',
        cursor: isLocked ? 'default' : 'pointer',
        bgcolor: isActive ? alpha(accentColor, 0.12) : 'transparent',
        '&:hover': isLocked
          ? {}
          : {
              bgcolor: isActive
                ? alpha(accentColor, 0.18)
                : alpha(theme.palette.action.hover, 0.06),
            },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.common.white}`,
          outlineOffset: 2,
        },
        transition: 'background-color 0.15s',
      }}
    >
      <Box sx={{ pt: 0.25 }}>
        <StepIcon step={step} status={status} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ mb: 0.25 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: isActive ? accentColor : 'text.disabled',
              fontWeight: 'fontWeightMedium',
            }}
          >
            Step {stepNumber}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              variant="caption"
              sx={{
                color: isActive ? accentColor : 'text.disabled',
                fontWeight: 'fontWeightMedium',
              }}
            >
              {stepTypeLabel(step)}
            </Typography>
            {isActive && (
              <Chip
                label={step.type === 'video' ? 'Now Playing' : 'Now Viewing'}
                size="small"
                sx={{
                  height: 18,
                  bgcolor: accentColor,
                  color: explorationMode
                    ? theme.palette.warning.contrastText
                    : theme.palette.primary.contrastText,
                  '& .MuiChip-label': {
                    px: 0.75,
                    fontSize: theme.typography.caption.fontSize,
                    fontWeight: 'fontWeightBold',
                  },
                }}
              />
            )}
          </Box>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: isLocked
              ? 'text.disabled'
              : isActive
                ? 'text.primary'
                : 'text.secondary',
            fontWeight: isActive ? 'fontWeightMedium' : 'fontWeightRegular',
            lineHeight: 1.35,
          }}
        >
          {step.name}
        </Typography>
      </Box>
    </Box>
  )
}

let ModeHeader = ({
  courseID,
  explorationMode,
}: {
  courseID: string
  explorationMode: boolean
}) => {
  let theme = useTheme()
  let dispatch = useAppDispatch()
  let accentColor = explorationMode
    ? theme.palette.warning.main
    : theme.palette.primary.main

  return (
    <Box
      sx={{
        px: 1.5,
        pt: 2,
        pb: 1.5,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        bgcolor: alpha(accentColor, 0.08),
        borderRadius: 1,
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: explorationMode ? 'warning.main' : 'success.main',
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 'fontWeightBold' }}>
            Mode:{' '}
            <Box component="span" sx={{ color: accentColor }}>
              {explorationMode ? 'Exploration' : 'Compliance'}
            </Box>
          </Typography>
          <Tooltip
            title={
              explorationMode
                ? 'Switch to Compliance mode to count this course toward your CE'
                : 'Switch to Exploration mode to browse freely — this will not count toward your CE'
            }
            // Tooltip clones its title onto its child as aria-label by
            // default, and aria-label has no business on an element without
            // a role that supports it. describeChild switches it to
            // aria-describedby instead — allowed on virtually any element —
            // and pairs correctly with the switch's own accessible name,
            // which is set directly below via slotProps.input['aria-label']
            // on the input that actually carries role="switch": one name,
            // one description, no attribute fighting for the same job.
            describeChild
          >
            <Switch
              size="small"
              checked={!explorationMode}
              onChange={(_, checked) =>
                dispatch(setExplorationMode({ courseID, enabled: !checked }))
              }
              slotProps={{
                input: {
                  'aria-label': explorationMode
                    ? 'Switch to Compliance mode to count this course toward your CE'
                    : 'Switch to Exploration mode to browse freely — this will not count toward your CE',
                },
              }}
              sx={
                explorationMode
                  ? {
                      '& .MuiSwitch-thumb': { bgcolor: 'warning.main' },
                      '& .MuiSwitch-track': { bgcolor: 'warning.dark' },
                    }
                  : undefined
              }
            />
          </Tooltip>
        </Box>
        {/* Both lines name CE directly rather than describing the mechanism
            behind it ("recorded"/"not recorded") — a mechanism word tells a
            learner nothing about what they actually stand to gain or lose,
            and this audience shouldn't have to translate it. Compliance's
            line also names the accreditor standard the mode satisfies, since
            "counts toward CE" alone doesn't say why. This is the compact
            restatement — the banner in course-player.tsx is the one that
            can't be missed, since this sidebar is behind a drawer on
            mobile. */}
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {explorationMode
            ? "Just browsing — this won't count toward your CE hours"
            : 'Counts toward your CE hours and meets accreditor compliance standards'}
        </Typography>
      </Box>
    </Box>
  )
}

// The assessment's seat in the stepper. Whether it's a preview of what's
// coming or a live destination is decided by the caller, never here:
// onGoToAssessment is only handed down when the wizard's own phase gate says
// the pre-quiz screen is reachable (every step complete, compliance mode). An
// unreachable milestone renders as plain text with nothing to click, so the
// affordance and the permission can't drift apart.
let FinalMilestone = ({
  content,
  isActive,
  onGoToAssessment,
}: {
  content: CourseV2Content
  isActive: boolean
  onGoToAssessment?: () => void
}) => {
  let theme = useTheme()

  if (!content.quiz) return null

  let isClickable = onGoToAssessment !== undefined

  return (
    <Box
      component={isClickable ? 'button' : 'div'}
      type={isClickable ? 'button' : undefined}
      onClick={onGoToAssessment}
      aria-current={isActive ? 'step' : undefined}
      sx={{
        display: 'block',
        width: '100%',
        mt: 4,
        p: 2,
        font: 'inherit',
        textAlign: 'left',
        color: 'inherit',
        borderRadius: 1,
        bgcolor: isActive
          ? alpha(theme.palette.primary.main, 0.12)
          : alpha(theme.palette.action.selected, 0.08),
        border: '1px solid',
        borderColor: isActive ? 'primary.main' : 'divider',
        cursor: isClickable ? 'pointer' : 'default',
        '&:hover': isClickable
          ? { bgcolor: alpha(theme.palette.primary.main, 0.18) }
          : {},
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.common.white}`,
          outlineOffset: 2,
        },
        transition: 'background-color 0.15s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <FinalMilestoneIcon fontSize="small" sx={{ color: 'primary.main' }} />
        <Typography
          variant="caption"
          sx={{
            color: 'primary.main',
            fontWeight: 'fontWeightBold',
          }}
        >
          Final Milestone
        </Typography>
        {isActive && (
          <Chip
            label="Current"
            size="small"
            sx={{
              height: 18,
              bgcolor: 'primary.main',
              color: theme.palette.primary.contrastText,
              '& .MuiChip-label': {
                px: 0.75,
                fontSize: theme.typography.caption.fontSize,
                fontWeight: 'fontWeightBold',
              },
            }}
          />
        )}
      </Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: 'fontWeightMedium', mb: 0.5 }}
      >
        {content.quiz.name}
      </Typography>
      <Typography variant="caption" sx={{ color: 'primary.main' }}>
        Answer {content.quiz.passingScore} of {content.quiz.displayedQuestions}{' '}
        correctly to pass
      </Typography>
    </Box>
  )
}

// What the Final Milestone becomes once there's nothing left to reach. Replaces
// it rather than sitting alongside it: a course whose certificate has been issued
// has no assessment left to advertise, and leaving the milestone up next to a
// completion card would ask the learner to work out which one is current.
//
// It says "Certificate Issued" only when the certificate genuinely exists — the
// caller drives this off the progress record's issued state, not off being in the
// certificate phase, which is merely where the learner waits for it. There is
// nothing to click: the certificate surface is already on screen beside this.
let CompletionCard = ({ content }: { content: CourseV2Content }) => {
  let theme = useTheme()

  return (
    <Box
      sx={{
        mt: 4,
        p: 2,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.success.main, 0.12),
        border: '1px solid',
        borderColor: 'success.main',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <ConfirmIcon fontSize="small" sx={{ color: 'success.main' }} />
        <Typography
          variant="caption"
          sx={{ color: 'success.main', fontWeight: 'fontWeightBold' }}
        >
          All Complete
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: 'fontWeightMedium', mb: 0.5 }}
      >
        Certificate Issued
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        You&apos;ve finished {content.name}.
      </Typography>
    </Box>
  )
}

export let CourseStepper = ({
  content,
  activeStepIndex,
  completedStepIndices,
  onSelectStep,
  isAssessmentActive = false,
  isCourseComplete = false,
  onGoToAssessment,
}: {
  content: CourseV2Content
  activeStepIndex: number | null
  completedStepIndices: number[]
  onSelectStep: (arrayIndex: number) => void
  isAssessmentActive?: boolean
  isCourseComplete?: boolean
  onGoToAssessment?: () => void
}) => {
  let explorationMode = useAppSelector(
    selectExplorationModeByCourseID(content.courseID),
  )

  let stepStatus = (arrayIndex: number): StepStatus => {
    if (completedStepIndices.includes(arrayIndex)) return 'completed'
    if (arrayIndex === activeStepIndex) return 'active'
    if (explorationMode) return 'unlocked'
    let firstIncomplete = content.steps.findIndex(
      (_, i) => !completedStepIndices.includes(i),
    )
    if (firstIncomplete !== -1 && arrayIndex > firstIncomplete) return 'locked'
    return 'unlocked'
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        px: 2,
        pt: 3,
        pb: 8,
      }}
    >
      <ModeHeader
        courseID={content.courseID}
        explorationMode={explorationMode}
      />
      <Divider sx={{ my: 3 }} />

      {content.steps.length > 0 && (
        <Typography
          variant="overline"
          sx={{
            pb: 0.5,
            display: 'block',
            color: 'text.disabled',
            lineHeight: 1.35,
          }}
        >
          {content.name}
        </Typography>
      )}

      <Box sx={{ mb: 3 }}>
        {content.steps.map((step, i) => (
          <StepRow
            key={step.id}
            step={step}
            stepNumber={i + 1}
            status={stepStatus(i)}
            arrayIndex={i}
            explorationMode={explorationMode}
            onSelectStep={onSelectStep}
          />
        ))}
      </Box>

      {/* Exploration mode has no assessment, survey, or certificate — none of
          that content exists for a free-roam visit, so the milestone is absent
          rather than locked. Showing it disabled would advertise a destination
          this mode can never reach. */}
      {!explorationMode && (
        <>
          <Divider />
          {isCourseComplete ? (
            <CompletionCard content={content} />
          ) : (
            <FinalMilestone
              content={content}
              isActive={isAssessmentActive}
              onGoToAssessment={onGoToAssessment}
            />
          )}
        </>
      )}
    </Box>
  )
}

export let CourseStepperSkeleton = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflowY: 'auto',
      px: 2,
      pt: 3,
      pb: 8,
    }}
  >
    <Skeleton
      variant="rounded"
      height={64}
      sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.08)' }}
    />
    <Divider sx={{ my: 3 }} />
    <Skeleton variant="text" width="60%" sx={{ mb: 1.5 }} />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
      {Array.from({ length: 4 }, (_, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1.5, px: 1.5, py: 1.5 }}>
          <Skeleton variant="circular" width={20} height={20} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="80%" />
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
)
